import { gql, useMutation } from "@apollo/client";
import classNames from "classnames";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CiroButton, { CiroButtonStyleEnum } from "../../../shared/CiroButton";
import CiroTextInput from "../../../shared/CiroTextInput";
import CiroSpinner from "../../../shared/CiroSpinner";
import CiroSwitch from "../../../shared/CiroSwitch";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import AutopilotSessionWebhookExportModalHeader from "./AutopilotSessionWebhookExportModalHeader";
import toast from "react-hot-toast";
import {
  AutopilotSessionWebhookExportModal_ExportToWebhookMutation,
  AutopilotSessionWebhookExportModal_ExportToWebhookMutationVariables,
} from "../../../../__generated__/graphql";
import CiroLink from "../../../shared/CiroLink";

interface IHttpHeader {
  key: string;
  value: string;
}

const AutopilotSessionWebhookExportModal_ExportToWebhook = gql`
  mutation AutopilotSessionWebhookExportModal_ExportToWebhook(
    $input: ExportToWebhookInput!
  ) {
    exportToWebhook(input: $input) {
      success
      message
      transactionId
    }
  }
`;

const validationSchema = yup.object({
  webhookUrl: yup
    .string()
    .url("Please enter a valid URL")
    .required("Webhook URL is required"),
  headers: yup.array().of(
    yup
      .object({
        key: yup.string(),
        value: yup.string(),
      })
      .test(
        "header-validation",
        "Both key and value are required",
        function (value) {
          const { key, value: headerValue } = value || {};
          if (
            (key && key.length > 0) ||
            (headerValue && headerValue.length > 0)
          ) {
            if (!key || key.length === 0) {
              return this.createError({
                path: "key",
                message: "Header key is required when value is provided",
              });
            }
            if (!headerValue || headerValue.length === 0) {
              return this.createError({
                path: "value",
                message: "Header value is required when key is provided",
              });
            }
          }
          return true;
        },
      ),
  ),
  skipPreviouslyExported: yup.boolean(),
});

interface IAutopilotSessionWebhookExportModalFormProps {
  autopilotSessionId: number;
  contactIds: number[] | null | undefined;
  onSuccess: (transactionId: number) => void;
  onClose: () => void;
}

interface IFormValues {
  webhookUrl: string;
  headers: IHttpHeader[];
  skipPreviouslyExported: boolean;
}

const AutopilotSessionWebhookExportModalForm = ({
  autopilotSessionId,
  contactIds,
  onSuccess,
  onClose,
}: IAutopilotSessionWebhookExportModalFormProps) => {
  const [exportToWebhook, { loading: exportLoading }] = useMutation<
    AutopilotSessionWebhookExportModal_ExportToWebhookMutation,
    AutopilotSessionWebhookExportModal_ExportToWebhookMutationVariables
  >(AutopilotSessionWebhookExportModal_ExportToWebhook);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      webhookUrl: "",
      headers: [] as IHttpHeader[],
      skipPreviouslyExported: true,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "headers",
  });

  const onSubmit = async (values: IFormValues) => {
    try {
      const validHeaders = values.headers.filter(
        (h: IHttpHeader) => h.key.trim() && h.value.trim(),
      );

      const response = await exportToWebhook({
        variables: {
          input: {
            autopilotSessionId,
            orgContactIds: contactIds ?? null,
            webhookUrl: values.webhookUrl.trim(),
            headers: validHeaders,
            skipContactsPreviouslyExported: values.skipPreviouslyExported,
          },
        },
      });

      if (response.data?.exportToWebhook.success) {
        onSuccess(response.data.exportToWebhook.transactionId || 0);
      } else {
        toast.error(response.data?.exportToWebhook.message || "Export failed");
      }
    } catch (error: any) {
      console.error(error);
      toast.error("Unexpected error, export failed");
    }
  };

  const webhookUrl = watch("webhookUrl");
  const skipPreviouslyExported = watch("skipPreviouslyExported");

  return (
    <div
      className={classNames("p-3", "flex", "flex-col", "gap-6")}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header with Title and Close Button */}
      <AutopilotSessionWebhookExportModalHeader
        title="Export to webhook"
        onClose={onClose}
      />

      {/* Description Text */}
      <div className={classNames("space-y-1")}>
        <p className={classNames("text-gray-600", "text-sm")}>
          Send your results to a webhook endpoint (e.g. Zapier or Clay). Need
          help? See guides for{" "}
          <CiroLink
            href="https://zapier.com/apps/webhook/integrations"
            shouldOpenInNewTab={true}
          >
            Zapier
          </CiroLink>{" "}
          and{" "}
          <CiroLink
            href="https://www.clay.com/university/guide/webhook-integration-guide"
            shouldOpenInNewTab={true}
          >
            Clay
          </CiroLink>
          .
        </p>
      </div>

      {/* Webhook URL Field */}
      <CiroTextInput
        name="webhookUrl"
        label="Webhook URL"
        labelClassName={classNames("text-sm", "font-medium", "text-gray-900")}
        register={control.register("webhookUrl")}
        placeholder="https://hooks.zapier.com/hooks/catch/..."
        error={errors.webhookUrl?.message}
      />

      {/* HTTP Headers Section */}
      <div className={classNames("space-y-3", "w-full")}>
        <div>
          <label
            className={classNames(
              "block",
              "text-sm",
              "font-medium",
              "text-gray-900",
            )}
          >
            HTTP headers
          </label>
          <div className={classNames("text-xs", "text-gray-500")}>Optional</div>
        </div>

        {fields.length > 0 && (
          <div className={classNames("space-y-2")}>
            {fields.map((field, index) => (
              <div
                key={field.id}
                className={classNames(
                  "flex",
                  "gap-2",
                  "items-center",
                  "w-full",
                )}
              >
                <CiroTextInput
                  register={control.register(`headers.${index}.key`)}
                  placeholder="Header name"
                  wrapperClassName={classNames("flex-1")}
                />
                <CiroTextInput
                  register={control.register(`headers.${index}.value`)}
                  placeholder="Header value"
                  wrapperClassName={classNames("flex-1")}
                />
                <CiroButton
                  onClick={() => remove(index)}
                  style={CiroButtonStyleEnum.UNSTYLED}
                  analyticsField="Remove header"
                >
                  <TrashIcon className={classNames("h-4", "w-4")} />
                </CiroButton>
              </div>
            ))}
          </div>
        )}

        <CiroButton
          analyticsField="Add header"
          onClick={() => append({ key: "", value: "" })}
          style={CiroButtonStyleEnum.PRIMARY}
        >
          <PlusIcon className={classNames("h-4", "w-4", "mr-1")} />
          Add header
        </CiroButton>

        <p className={classNames("text-xs", "text-gray-500", "mt-2")}>
          Common headers: Authorization, X-API-Key
        </p>
      </div>

      {/* Checkbox Option */}
      <div className={classNames("space-y-2")}>
        <div className={classNames("flex", "items-center", "gap-3")}>
          <CiroSwitch
            checked={skipPreviouslyExported}
            onChange={(checked) => setValue("skipPreviouslyExported", checked)}
          />
          <label
            className={classNames("text-sm", "font-medium", "text-gray-700")}
          >
            Skip contacts previously exported
          </label>
        </div>
        <p className={classNames("text-xs", "text-gray-500")}>
          Only send new contacts that haven't been sent to this webhook before
        </p>
      </div>

      {/* Confirm Button */}
      <CiroButton
        analyticsField="Confirm Webhook Export"
        onClick={handleSubmit(onSubmit)}
        disabled={exportLoading || !webhookUrl?.trim()}
        style={CiroButtonStyleEnum.LOUD}
        customClassName={classNames(
          "w-full",
          exportLoading || !webhookUrl?.trim() ? "opacity-50" : "",
        )}
      >
        {exportLoading ? (
          <div className={classNames("flex", "items-center", "justify-center")}>
            <CiroSpinner loading={true} />
            <span className={classNames("ml-2")}>Exporting...</span>
          </div>
        ) : (
          "Confirm"
        )}
      </CiroButton>
    </div>
  );
};

export default AutopilotSessionWebhookExportModalForm;
